import React, { useEffect, useState } from 'react';
import { Table, Autocomplete, BodyText, Flex } from '@gaiads/telia-react-component-library';
import { Country, FinanceProduct } from '@cdaas/api-clients/dist/product-service-openapi';

import { TeliaFinanceGuidChangeParam } from '../utils';

interface FinanceProductIdRowProps {
  country: Country;
  financeProductIds: FinanceProduct[];
  currentlySetFinanceProductId: string;
  handleFinanceProductIdChange: (financeProductId: TeliaFinanceGuidChangeParam) => void;
}

export default function FinanceProductIdRow({
  country,
  financeProductIds,
  currentlySetFinanceProductId,
  handleFinanceProductIdChange,
}: FinanceProductIdRowProps) {
  const [currentFinanceProductNameText, setCurrentFinanceProductNameText] = useState<string>('');

  useEffect(() => {
    const currentFinanceProduct = financeProductIds.find(
      financeProduct => currentlySetFinanceProductId && financeProduct?.product_ids?.[country.code] === currentlySetFinanceProductId,
    );
    if (currentFinanceProduct) {
      setCurrentFinanceProductNameText(currentFinanceProduct.name);
    }
  }, [country.code, financeProductIds, currentlySetFinanceProductId]);

  const filterFinanceProductIds = (financeProductNameText: string) =>
    financeProductIds.filter(financeProduct => financeProduct.name.toLowerCase().includes(financeProductNameText.toLowerCase()));
  const [filteredFinanceProductIds, setFilteredFinanceProductIds] = useState<FinanceProduct[]>([]);

  // Clear possible existing finance id of the specific country
  const handleFinanceProductIdClear = () => {
    handleFinanceProductIdChange({ country_id: country.id, finance_product_id: undefined });
  };

  const handleTextChange = (financeProductNameText: string) => {
    setCurrentFinanceProductNameText(financeProductNameText);
    setFilteredFinanceProductIds(filterFinanceProductIds(financeProductNameText));
    // When user starts typing, clear possible finance id set earlier
    if (currentlySetFinanceProductId) {
      handleFinanceProductIdClear();
    }

    const foundProductFromEnteredText = financeProductIds.find(productId => productId.name === financeProductNameText)?.product_ids[country.code];
    if (foundProductFromEnteredText !== currentlySetFinanceProductId) {
      handleFinanceProductIdChange({ country_id: country.id, finance_product_id: foundProductFromEnteredText });
    }
  };

  return (
    <Table.Row key={country.id} data-testid={`finance-product-row-${country.id}`}>
      <Table.Cell>
        <Flex centeredVertically>
          <BodyText>{country.name}</BodyText>
        </Flex>
      </Table.Cell>
      <Table.Cell>
        <Autocomplete
          iconName={'search'}
          name={`tfFinanceId-${country.id}`}
          label={''}
          showCustomLabel
          options={filteredFinanceProductIds
            .filter(financeProduct => financeProduct.product_ids[country.code])
            .map(product => ({ label: product.name, value: product.product_ids[country.code] }))}
          onChange={(_, financeProductNameText) => handleTextChange(financeProductNameText)}
          onOptionSelect={(_, financeProductOption) => handleTextChange(financeProductOption.label)}
          value={currentFinanceProductNameText}
          type="search"
          autocomplete="off"
        />
      </Table.Cell>
      <Table.Cell>
        <BodyText>{financeProductIds.find(productId => productId.name === currentFinanceProductNameText)?.product_ids[country.code]}</BodyText>
      </Table.Cell>
    </Table.Row>
  );
}
